import React, { useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { updateEmployee, deleteEmployee } from '../services/firestoreService';
import { generateEmployeeProfilePDF } from '../services/pdfService';
import RecordAuditBlock from '../components/common/RecordAuditBlock';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import Modal from '../components/common/Modal';
import Card from '../components/common/Card';
import Loading from '../components/common/Loading';
import { DEPARTMENTS } from '../utils/constants';
import {
  formatCurrency,
  formatDate,
  parseDateInputValue,
  getEmployeeWorkHistory,
  getShiftPeriodLabel,
  getServiceTypeLabel
} from '../utils/helpers';
import toast from 'react-hot-toast';

const formatStartDateField = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  if (value?.seconds) return new Date(value.seconds * 1000).toISOString().slice(0, 10);
  if (value?.toDate) return value.toDate().toISOString().slice(0, 10);
  return '';
};

const EmployeeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const auditActor = user ? { uid: user.uid, email: user.email } : null;
  const { employees, loadingEmployees, expenses, invoices, quotations } = useApp();

  const employee = useMemo(() => employees.find((e) => e.id === id), [employees, id]);

  const history = useMemo(() => {
    if (!employee) return { expenses: [], shifts: [] };
    return getEmployeeWorkHistory(employee, { expenses, invoices, quotations });
  }, [employee, expenses, invoices, quotations]);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', department: '', startWorkDate: '' });

  const openEdit = () => {
    if (!employee) return;
    setFormData({
      name: employee.name,
      phone: employee.phone,
      department: employee.department,
      startWorkDate: formatStartDateField(employee.startWorkDate)
    });
    setShowEditModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!employee) return;
    setSubmitting(true);
    const result = await updateEmployee(employee.id, {
      name: formData.name,
      phone: formData.phone,
      department: formData.department,
      startWorkDate: parseDateInputValue(formData.startWorkDate),
      auditActor
    });
    if (result.success) {
      toast.success('Waa la cusbooneysiiyay');
      setShowEditModal(false);
    } else {
      toast.error(result.error);
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    if (!employee) return;
    setSubmitting(true);
    const result = await deleteEmployee(employee.id);
    if (result.success) {
      toast.success('Waa la tirtiray');
      navigate('/admin/employees');
    } else {
      toast.error(result.error);
    }
    setSubmitting(false);
  };

  const handlePdf = async () => {
    if (!employee) return;
    setDownloadingPdf(true);
    try {
      await generateEmployeeProfilePDF(employee, history);
      toast.success('PDF waa la soo dejiyay');
    } catch {
      toast.error('PDF khalad');
    }
    setDownloadingPdf(false);
  };

  if (loadingEmployees) return <Loading fullScreen />;

  if (!employee) {
    return (
      <div className="max-w-lg mx-auto text-center py-16 space-y-4">
        <p className="text-gray-600">Shaqaalaha lama helin.</p>
        <Button onClick={() => navigate('/admin/employees')}>← Liiska shaqaalaha</Button>
      </div>
    );
  }

  const recordPath = (s) =>
    s.kind === 'invoice' ? `/admin/invoices/${s.recordId}` : `/admin/quotations/${s.recordId}`;

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex flex-wrap gap-2 justify-between items-center">
        <Button variant="ghost" onClick={() => navigate('/admin/employees')}>← Liiska shaqaalaha</Button>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handlePdf} loading={downloadingPdf}>Soo deji PDF</Button>
          <Button variant="outline" onClick={openEdit}>Wax ka beddel</Button>
          <Button variant="danger" onClick={() => setShowDeleteModal(true)}>Tirtir</Button>
        </div>
      </div>

      <Card title={employee.name} icon={<span className="text-2xl">👤</span>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <p><span className="text-gray-500">Telefoon:</span> <span className="font-medium">{employee.phone}</span></p>
          <p><span className="text-gray-500">Qaybta:</span> <span className="font-medium">{employee.department}</span></p>
          <p><span className="text-gray-500">Bilaabay shaqada:</span> {formatDate(employee.startWorkDate) || '—'}</p>
          <p><span className="text-gray-500">Shaqooyin (kharash):</span> {employee.jobsHandled || 0}</p>
          <p><span className="text-gray-500">Kharash wadar:</span>{' '}
            <span className="font-semibold text-green-700">{formatCurrency(employee.revenueInvolved || 0)}</span>
          </p>
        </div>
        <RecordAuditBlock record={employee} />
      </Card>

      <Card title={`Shifts / guurimo (${history.shifts.length})`} icon={<span>🕐</span>}>
        {history.shifts.length === 0 ? (
          <p className="text-gray-500 text-sm">Wali shift lama qorin quotation/invoice.</p>
        ) : (
          <ul className="divide-y border rounded-lg">
            {history.shifts.map((s, i) => (
              <li key={`${s.recordId}-${i}`} className="p-4 text-sm space-y-1 hover:bg-slate-50">
                <div className="flex flex-wrap items-center gap-2">
                  <Link to={recordPath(s)} className="font-mono text-red-700 font-semibold hover:underline">
                    {s.ref}
                  </Link>
                  <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{s.kind === 'invoice' ? 'Invoice' : 'Quotation'}</span>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">{s.role}</span>
                  {s.shiftPeriod && (
                    <span className="text-xs text-gray-600">{getShiftPeriodLabel(s.shiftPeriod)}</span>
                  )}
                </div>
                <p className="text-gray-800">{s.customer}</p>
                <p className="text-gray-600 text-xs">
                  {s.shiftDate ? formatDate(s.shiftDate) : formatDate(s.movingDate)}
                  {s.startTime && s.endTime ? ` · ${s.startTime} – ${s.endTime}` : ''}
                  {s.serviceType ? ` · ${getServiceTypeLabel(s.serviceType)}` : ''}
                </p>
                {(s.fromLocation || s.toLocation) && (
                  <p className="text-gray-500 text-xs">{s.fromLocation} → {s.toLocation}</p>
                )}
                {(s.vehicleName || s.targoNumber || s.cartonCount) && (
                  <p className="text-gray-500 text-xs">
                    {[s.vehicleName, s.targoNumber ? `Targo ${s.targoNumber}` : '', s.cartonCount ? `Cartons ${s.cartonCount}` : '']
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title={`Kharashyada (${history.expenses.length})`} icon={<span>💵</span>}>
        {history.expenses.length === 0 ? (
          <p className="text-gray-500 text-sm">Kharash lama xiriirin.</p>
        ) : (
          <ul className="divide-y border rounded-lg">
            {history.expenses.map((ex) => (
              <li key={ex.id} className="p-3 flex justify-between gap-4 text-sm">
                <div>
                  <p className="font-medium">{ex.description || ex.category}</p>
                  <p className="text-xs text-gray-500">{formatDate(ex.date)} · {ex.category}</p>
                </div>
                <span className="font-semibold text-green-700">{formatCurrency(ex.amount)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Wax ka bedel shaqaalaha">
        <form onSubmit={handleSave} className="space-y-4">
          <Input label="Magaca" name="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
          <Input label="Telefoon" name="phone" type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required />
          <Select label="Qaybta" name="department" value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} options={DEPARTMENTS} required />
          <Input label="Taariikhda uu bilaabay" name="startWorkDate" type="date" value={formData.startWorkDate} onChange={(e) => setFormData({ ...formData, startWorkDate: e.target.value })} />
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={() => setShowEditModal(false)} fullWidth>Ka noqo</Button>
            <Button type="submit" loading={submitting} fullWidth>Kaydi</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Tirtir shaqaalaha" size="sm">
        <p className="text-gray-600 text-sm mb-4">Ma hubtaa inaad tirtirto <strong>{employee.name}</strong>?</p>
        <div className="flex gap-3">
          <Button variant="ghost" onClick={() => setShowDeleteModal(false)} fullWidth>Maya</Button>
          <Button variant="danger" onClick={handleDelete} loading={submitting} fullWidth>Haa, tirtir</Button>
        </div>
      </Modal>
    </div>
  );
};

export default EmployeeDetail;
